import { getCleanStartorEndDay } from "helpers/utils";
import type { IAdvanceSlice } from "store/advancePaymentSlice";
import type { ITermBeneficiar } from "store/managementStore";
import type { IPayment } from "store/paymentSlice";
import type { TFilterParamsPaymentStatus } from "store/types";
import type { IDatesRange } from "types";

import type { IPaymentPageData } from ".";

/**
 * Used to check if payment includes IBAN information or nothing specified
 **/
export const isPaidWithIBAN = (payment: IPayment | ITermBeneficiar | IAdvanceSlice) => {
    if (payment.iban && payment.iban !== "") {
        return true;
    } else {
        return payment.bankCheck === 0 && !payment.paypalEmail;
    }
};

/**
 * Updates session storage.
 */
export const updatePaymentPageData = (key: keyof IPaymentPageData, value: any) => {
    const oldData = JSON.parse(sessionStorage.getItem("payment_page_data") ?? "{}");

    oldData[key] = value;

    sessionStorage.setItem("payment_page_data", JSON.stringify(oldData));
};

/**
 * Default upper bound for payment filters: latest of a configured boundary (e.g. last period end), today,
 * and newest payment date from API (covers future-dated payments beyond periods).
 */
export const resolveDefaultPaymentFilterEndDate = (
    configuredRangeEnd: Date,
    newestPaymentDateIso: string | null | undefined,
): Date => {
    const endOfToday = getCleanStartorEndDay(new Date(), { time: "end" });
    const endOfNewestPayment = newestPaymentDateIso
        ? getCleanStartorEndDay(newestPaymentDateIso, { time: "end" })
        : null;
    const endTimestamp = Math.max(
        configuredRangeEnd.getTime(),
        endOfToday.getTime(),
        endOfNewestPayment?.getTime() ?? Number.NEGATIVE_INFINITY,
    );
    return getCleanStartorEndDay(new Date(endTimestamp), { time: "end" });
};

/**
 * Book portal — auto range from period bounds + payment API dates (no session-stored override).
 */
export const buildBookAutoDatesRangeFromPeriods = (
    periodsBounds: Pick<IDatesRange, "startDate" | "endDate">,
    firstPaymentDate: Date,
    newestPaymentDateIso: string | null | undefined,
): IDatesRange => {
    const firstPeriodStart = getCleanStartorEndDay(periodsBounds.startDate);
    const endOfLastConfiguredPeriod = getCleanStartorEndDay(periodsBounds.endDate, { time: "end" });
    const endDate = resolveDefaultPaymentFilterEndDate(endOfLastConfiguredPeriod, newestPaymentDateIso);
    const startDate = firstPeriodStart > firstPaymentDate ? firstPaymentDate : firstPeriodStart;
    return { startDate, endDate };
};

export const convertNumberToPaymentStatus = (value: number): TFilterParamsPaymentStatus => {
    switch (value) {
        case 0:
            return "unpaid";
        case 1:
            return "topay";
        case 2:
            return "paid";
        default:
            return "unpaid";
    }
};

/**
 * Formats payment dates correctly for sending to backend.
 */
export const getDatesForPaymentsFetch = ({
    isBookCompany,
    periodsDate,
    newDates,
}: {
    isBookCompany: boolean;
    periodsDate: IDatesRange;
    newDates: IDatesRange;
}) => {
    if (!periodsDate || !newDates) return;
    let dates: any;
    if (isBookCompany) {
        const periodStart = new Date(periodsDate.startDate);
        const periodEnd = new Date(periodsDate.endDate);
        // Some mysterous clients have a period that starts in the future :) we need to handle that.
        dates = {
            startDate: (newDates.startDate < periodStart ? newDates.startDate : periodStart).toLocaleDateString(
                "en-CA",
            ),
            endDate: (newDates.endDate < periodEnd ? periodEnd : newDates.endDate).toLocaleDateString("en-CA"),
        };
    } else {
        dates = {
            startDate: newDates.startDate.toLocaleDateString("en-CA"),
            endDate: newDates.endDate.toLocaleDateString("en-CA"),
        };
    }
    return dates;
};
